/**
 * apiResponse.js — Shared API response parsing utilities
 * Standardizes how the web app reads backend responses to match
 * the mobile app's approach.
 *
 * Backend always returns:
 * Success: { success: true, message: "...", data: { ... } }
 * Error:   { success: false, message: "..." }
 */

export const getData = (response) => {
  return response?.data?.data ?? response?.data ?? null;
};

export const getErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  // Per Asnake: backend validation endpoints (password strength,
  // express-validator field checks, etc.) return a generic top-level
  // `message` ("Validation failed", "Password validation failed") PLUS
  // a specific `errors` array -- but this only ever surfaced the
  // generic one, so users saw "Password validation failed" instead of
  // "Password must be at least 8 characters long". `errors` entries
  // come in two shapes depending on which backend check produced them:
  // express-validator gives { msg: "..." } objects, while the password
  // strength check gives plain strings -- `e?.msg || e` handles both.
  const specificErrors = error?.response?.data?.errors;
  if (Array.isArray(specificErrors) && specificErrors.length > 0) {
    const msgs = specificErrors.map((e) => (typeof e === 'string' ? e : e?.msg)).filter(Boolean);
    if (msgs.length > 0) return msgs.join('. ');
  }
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

export const isSuccess = (response) => {
  return response?.data?.success === true;
};

export const getPaginatedData = (response) => {
  const data = response?.data?.data;
  if (Array.isArray(data)) {
    return {
      items: data,
      pagination: response?.data?.pagination || { page: 1, limit: data.length, total: data.length },
    };
  }
  const keys = Object.keys(data || {});
  const listKey = keys.find(k => Array.isArray(data[k]));
  return {
    items: listKey ? data[listKey] : [],
    pagination: data?.pagination || response?.data?.pagination || {},
  };
};

export const isEmpty = (data) => {
  if (!data) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data).length === 0;
  return false;
};

export const parseAuthResponse = (response) => {
  if (!isSuccess(response)) {
    throw new Error(response?.data?.message || 'Authentication failed');
  }
  const data = getData(response);
  if (!data?.accessToken) {
    throw new Error('Invalid authentication response from server');
  }
  return {
    user: data.user,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
};
